import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RiFileCopyLine, RiCheckLine, RiDeleteBinLine, RiUserAddLine } from '@remixicon/react'
import {
  WORKSPACE_ROLES,
  type Invitation,
  type InvitationCreated,
  type InvitationEmailStatus,
  type WaitingInvite,
  type WorkspaceMember,
  type WorkspaceRole,
} from 'shared'
import {
  Alert,
  Badge,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  Input,
  SectionSpinner,
  Select,
} from '../ui'
import { api } from '../../utils/api'
import { toErrorMessage } from '../../lib/errors'
import { formatDate } from '../../lib/dates'
import { cx, focusRing } from '../../lib/utils'

/**
 * Come è andata l'email di un invito, in una parola.
 *
 * `disabled` e `failed` non sono la stessa cosa e non devono sembrarlo: il
 * primo dice che il canale non è configurato — normale, si consegna il link a
 * mano — il secondo che si è provato e il provider ha detto di no.
 */
function InvitationEmailState({ status }: { status: InvitationEmailStatus | null }) {
  const { t } = useTranslation()

  if (!status) return null

  const tone = status === 'sent' ? 'success' : status === 'failed' ? 'warning' : 'neutral'

  return <Badge variant={tone}>{t(`members.email_${status}`)}</Badge>
}

export default function WorkspaceMembersPanel({
  workspaceId,
  canManage,
  apiBase = '/api/workspaces',
  canEditMembers = canManage,
}: {
  workspaceId: string
  /** May invite, resend and revoke. */
  canManage: boolean
  /**
   * Where the workspace's people and invitations are served. The operator side
   * uses the default; a partner's own area has a prefix of its own.
   */
  apiBase?: string
  /**
   * May change a member's role or remove them. Separate from inviting: a
   * partner does the one and not the other.
   */
  canEditMembers?: boolean
}) {
  const { t, i18n } = useTranslation()
  const roleOptions = WORKSPACE_ROLES.map((value) => ({
    value,
    label: t(`members.role_${value}`),
  }))
  const [members, setMembers] = useState<WorkspaceMember[]>([])
  const [invitations, setInvitations] = useState<Invitation[]>([])
  // Accepted through onboarding and not yet invited. Only the operator's own API knows them.
  const [waiting, setWaiting] = useState<WaitingInvite[]>([])
  const [invitingWaiting, setInvitingWaiting] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [role, setRole] = useState<WorkspaceRole>('member')
  const [inviting, setInviting] = useState(false)
  const [inviteUrl, setInviteUrl] = useState<string | null>(null)
  // L'esito dell'email che accompagna quel link. Il link si mostra comunque:
  // la consegna a mano era l'unica strada prima e resta quella di riserva.
  const [inviteEmail, setInviteEmail] = useState<{
    status: InvitationEmailStatus | null
    detail: string | null
  } | null>(null)
  const [resending, setResending] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const load = useCallback(async () => {
    const requests: Promise<unknown>[] = [
      api
        .get<{ data: WorkspaceMember[] }>(`${apiBase}/${workspaceId}/members`)
        .then((response) => setMembers(response.data)),
    ]

    if (canManage) {
      requests.push(
        api
          .get<{ data: Invitation[] }>(`${apiBase}/${workspaceId}/invitations`)
          .then((response) => setInvitations(response.data.filter((i) => i.status === 'pending')))
      )
    }

    if (canManage && apiBase === '/api/workspaces') {
      requests.push(
        api
          .get<{ data: WaitingInvite[] }>(`/api/onboarding/workspaces/${workspaceId}/waiting`)
          .then((response) => setWaiting(response.data))
      )
    }

    await Promise.all(requests)
  }, [workspaceId, canManage, apiBase])

  useEffect(() => {
    load()
      .catch((err) => setError(toErrorMessage(err) ?? 'error'))
      .finally(() => setLoading(false))
  }, [load])

  const handleInvite = async (event: React.FormEvent) => {
    event.preventDefault()
    setInviting(true)
    setError(null)
    setInviteUrl(null)
    setInviteEmail(null)
    setCopied(false)

    try {
      const response = await api.post<{ data: InvitationCreated }>(
        `${apiBase}/${workspaceId}/invitations`,
        { email, name: name || undefined, role }
      )
      setInviteUrl(response.data.invite_url)
      setInviteEmail({
        status: response.data.email_status,
        detail: response.data.email_detail,
      })
      setEmail('')
      setName('')
      setRole('member')
      await load()
    } catch (err) {
      setError(toErrorMessage(err) ?? 'error')
    } finally {
      setInviting(false)
    }
  }

  const copyInviteUrl = async () => {
    if (!inviteUrl) return
    await navigator.clipboard.writeText(inviteUrl)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const changeRole = async (userId: string, nextRole: WorkspaceRole) => {
    const previous = members
    setMembers((current) =>
      current.map((m) => (m.user_id === userId ? { ...m, role: nextRole } : m))
    )
    setError(null)

    try {
      await api.put(`${apiBase}/${workspaceId}/members/${userId}`, { role: nextRole })
    } catch (err) {
      // Put the select back where it was: the server refused the change.
      setMembers(previous)
      setError(toErrorMessage(err) ?? 'error')
    }
  }

  const inviteWaiting = async (applicationId: string) => {
    setError(null)
    setInvitingWaiting(applicationId)
    try {
      const response = await api.post<{
        data: { invite_url: string; email_status: InvitationEmailStatus | null; email_detail: string | null }
      }>(`/api/onboarding/applications/${applicationId}/invite`, {})
      setInviteUrl(response.data.invite_url)
      setInviteEmail({ status: response.data.email_status, detail: response.data.email_detail })
      setCopied(false)
      await load()
    } catch (err) {
      setError(toErrorMessage(err) ?? 'error')
    } finally {
      setInvitingWaiting(null)
    }
  }

  const removeMember = async (userId: string) => {
    setError(null)
    try {
      await api.delete(`${apiBase}/${workspaceId}/members/${userId}`)
      await load()
    } catch (err) {
      setError(toErrorMessage(err) ?? 'error')
    }
  }

  const resendInvitation = async (invitationId: string) => {
    setError(null)
    setResending(invitationId)
    try {
      await api.post(`${apiBase}/${workspaceId}/invitations/${invitationId}/resend`, {})
      await load()
    } catch (err) {
      setError(toErrorMessage(err) ?? 'error')
    } finally {
      setResending(null)
    }
  }

  const revokeInvitation = async (invitationId: string) => {
    setError(null)
    try {
      await api.delete(`${apiBase}/${workspaceId}/invitations/${invitationId}`)
      await load()
    } catch (err) {
      setError(toErrorMessage(err) ?? 'error')
    }
  }

  if (loading) return <SectionSpinner />

  return (
    <div className="space-y-6">
      {error && <Alert>{error === 'error' ? t('errors.generic') : error}</Alert>}

      {canManage && (
        <Card className="p-6">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-50">
            {t('members.inviteTitle')}
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {t('members.inviteDescription')}
          </p>

          <form onSubmit={handleInvite} className="mt-4 grid gap-3 sm:grid-cols-[2fr,1.5fr,1fr,auto]">
            <div>
              <label htmlFor="invite-email" className="sr-only">
                {t('auth.email')}
              </label>
              <Input
                id="invite-email"
                type="email"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder={t('members.emailPlaceholder')}
              />
            </div>
            <div>
              <label htmlFor="invite-name" className="sr-only">
                {t('auth.name')}
              </label>
              <Input
                id="invite-name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder={t('members.namePlaceholder')}
              />
            </div>
            <div>
              <label htmlFor="invite-role" className="sr-only">
                {t('members.role')}
              </label>
              <Select
                id="invite-role"
                value={role}
                onChange={(event) => setRole(event.target.value as WorkspaceRole)}
                options={roleOptions}
              />
            </div>
            <Button type="submit" loading={inviting}>
              <RiUserAddLine className="-ml-1 mr-1.5 size-4" aria-hidden="true" />
              {t('members.invite')}
            </Button>
          </form>

          {inviteUrl && (
            <div className="mt-4 rounded-md border border-primary-200 bg-primary-50 p-4 dark:border-primary-900 dark:bg-primary-500/10">
              <p className="text-sm font-medium text-primary-900 dark:text-primary-200">
                {inviteEmail?.status === 'sent'
                  ? t('members.inviteSent')
                  : t('members.inviteReady')}
              </p>
              <p className="mt-0.5 text-xs text-primary-700 dark:text-primary-300">
                {inviteEmail?.status === 'sent'
                  ? t('members.inviteSentHelp')
                  : t('members.inviteReadyHelp')}
              </p>
              {/* Il motivo del rifiuto vale più della parola "fallito": dice se
                  manca un dominio da verificare o se è caduta la rete. */}
              {inviteEmail?.status === 'failed' && inviteEmail.detail && (
                <p className="mt-2 text-xs text-amber-700 dark:text-amber-400">
                  {inviteEmail.detail}
                </p>
              )}
              <div className="mt-3 flex gap-2">
                <Input readOnly value={inviteUrl} className="font-mono text-xs" />
                <Button type="button" variant="secondary" onClick={copyInviteUrl}>
                  {copied ? (
                    <RiCheckLine className="size-4 text-emerald-600" aria-hidden="true" />
                  ) : (
                    <RiFileCopyLine className="size-4" aria-hidden="true" />
                  )}
                  <span className="sr-only">{t('common.copy')}</span>
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}

      <Card className="p-6">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-50">
          {t('members.title')}
        </h3>

        {members.length === 0 ? (
          <EmptyState title={t('members.emptyTitle')} description={t('members.emptyDescription')} />
        ) : (
          <ul className="mt-4 divide-y divide-gray-100 dark:divide-gray-800">
            {members.map((member) => (
              <li key={member.user_id} className="flex items-center gap-4 py-3">
                <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-gray-100 text-sm font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                  {member.name.charAt(0).toUpperCase()}
                </span>

                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-50">
                    {member.name}
                    {member.title && (
                      <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
                        {member.title}
                      </span>
                    )}
                  </p>
                  <p className="truncate text-sm text-gray-500 dark:text-gray-400">
                    {member.email}
                  </p>
                </div>

                <span className="hidden text-xs text-gray-400 dark:text-gray-500 sm:block">
                  {formatDate(member.joined_at, i18n.language)}
                </span>

                {canEditMembers ? (
                  <>
                    {/* Select wraps itself in a full-width block, so the row
                        constrains it here rather than on the control. */}
                    <div className="w-36 shrink-0">
                      <Select
                        value={member.role}
                        aria-label={t('members.role')}
                        onChange={(event) =>
                          changeRole(member.user_id, event.target.value as WorkspaceRole)
                        }
                        options={roleOptions}
                      />
                    </div>
                    <ConfirmDialog
                      title={t('members.removeTitle')}
                      description={t('members.removeDescription', { name: member.name })}
                      confirmLabel={t('members.remove')}
                      onConfirm={() => removeMember(member.user_id)}
                      trigger={
                        <button
                          type="button"
                          aria-label={t('members.remove')}
                          className={cx(
                            'rounded-md p-2 text-gray-400 transition hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-500/10',
                            focusRing,
                          )}
                        >
                          <RiDeleteBinLine className="size-4" aria-hidden="true" />
                        </button>
                      }
                    />
                  </>
                ) : (
                  <Badge variant="neutral">{t(`members.role_${member.role}`)}</Badge>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>

      {canManage && waiting.length > 0 && (
        <Card className="p-6">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-50">
            {t('members.waitingTitle')}
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {t('members.waitingDescription')}
          </p>
          <ul className="mt-4 divide-y divide-gray-100 dark:divide-gray-800">
            {waiting.map((person) => (
              <li key={person.application_id} className="flex items-center gap-4 py-3">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-50">
                    {person.person_name}
                  </p>
                  <p className="truncate text-sm text-gray-500 dark:text-gray-400">{person.email}</p>
                </div>
                <Badge variant="warning">{t('members.toInvite')}</Badge>
                <Button
                  type="button"
                  variant="secondary"
                  loading={invitingWaiting === person.application_id}
                  onClick={() => inviteWaiting(person.application_id)}
                  aria-label={t('members.inviteNamed', { name: person.person_name })}
                >
                  {t('members.invite')}
                </Button>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {canManage && invitations.length > 0 && (
        <Card className="p-6">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-50">
            {t('members.pendingTitle')}
          </h3>
          <ul className="mt-4 divide-y divide-gray-100 dark:divide-gray-800">
            {invitations.map((invitation) => (
              <li key={invitation.id} className="flex items-center gap-4 py-3">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-50">
                    {invitation.email}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {t('members.expiresOn', {
                      date: formatDate(invitation.expires_at, i18n.language),
                    })}
                  </p>
                </div>
                <InvitationEmailState status={invitation.email_status} />
                <Badge variant="neutral">{t(`members.role_${invitation.role}`)}</Badge>
                <Button
                  type="button"
                  variant="ghost"
                  loading={resending === invitation.id}
                  onClick={() => resendInvitation(invitation.id)}
                >
                  {t('members.resend')}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => revokeInvitation(invitation.id)}
                >
                  {t('members.revoke')}
                </Button>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  )
}
